/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the complyvigilance product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  asml_logo,
  nl_logo,
  openchain_logo,
  complyvigilance_logo,
  wpc_logo,
  normeringarbeid_logo,
  hcl_logo,
  ingBank_logo,
  nxp_logo,
  shell_logo,
  philips_logo,
  comsats_logo,
  googlePartner_logo,
} from "../assets/svg";

const ClientsSlider = () => {
  const { t } = useTranslation();
  const images = [
    {
      client_icon: complyvigilance_logo,
      client_url: "https://complyvigilance.com/",
    },
    {
      client_icon: wpc_logo,
      client_url: "https://workpermitconsultancy.com/",
    },
    {
      client_icon: openchain_logo,
      client_url:
        "https://www.openchainproject.org/featured/2021/01/13/sequenx-is-the-latest-openchain-partner",
    },
    {
      client_icon: normeringarbeid_logo,
      client_url:
        "https://www.normeringarbeid.nl/snakeurmerk/gecertificeerde-ondernemingen?q=SeQuenX&filters%5Bsector%5D=&filters%5Bkvk%5D=&filters%5Bnormen%5D=",
    },
    {
      client_icon: asml_logo,
      client_url: "https://www.asml.com/en",
    },
    // {
    //   client_icon: nl_logo,
    //   client_url:
    //     "https://ind.nl/en/public-register-recognised-sponsors/public-register-regular-labour-and-highly-skilled-migrants",
    // },
    {
      client_icon: ingBank_logo,
      client_url: "https://www.ing.com/Home.htm",
    },
    {
      client_icon: nxp_logo,
      client_url: "https://www.nxp.com/",
    },
    {
      client_icon: hcl_logo,
      client_url: "https://www.hcltech.com/",
    },
    {
      client_icon: shell_logo,
      client_url: "https://www.shell.nl/",
    },
    {
      client_icon: philips_logo,
      client_url: "https://www.philips.nl/",
    },
    {
      client_icon: comsats_logo,
      client_url:
        "https://comsats.org/comsats-and-dutch-it-company-sequenx-to-establish-technology-corridor-cntc/",
    },
    {
      client_icon: googlePartner_logo,
      client_url: "https://ads.google.com/home/partners/become-a-partner/",
    },
  ];
  const [slidesToShow, setSlidesToShow] = useState(4);

  useEffect(() => {
    const updateSlidesToShow = () => {
      // Set the number of slides to show based on the screen width
      if (window.innerWidth >= 1200) {
        setSlidesToShow(4);
      } else if (window.innerWidth >= 900) {
        setSlidesToShow(3);
      } else if (window.innerWidth >= 600) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(1);
      }
    };

    // Call the updateSlidesToShow function on initial render
    updateSlidesToShow();

    // Call the updateSlidesToShow function whenever the screen is resized
    window.addEventListener("resize", updateSlidesToShow);

    // Clean up the event listener on component unmount
    return () => window.removeEventListener("resize", updateSlidesToShow);
  }, []);

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="h-px w-10 bg-cyan-400" />
            <span className="text-xs tracking-[0.3em] uppercase text-cyan-500 font-medium">
              {t("clientsSlider.topHeading")}
            </span>
            <span className="h-px w-10 bg-cyan-400" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t("clientsSlider.heading")}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t("clientsSlider.description")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl shadow-sm p-8"
        >
          <Slider {...sliderSettings}>
            {images.map((imageUrl, index) => (
              <div key={index} className="px-4 py-2">
                <motion.a
                  href={imageUrl.client_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="block p-4 h-24 flex items-center justify-center"
                >
                  <img
                    className="max-h-16 max-w-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                    src={imageUrl.client_icon}
                    alt={`Client ${index + 1}`}
                  />
                </motion.a>
              </div>
            ))}
          </Slider>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientsSlider;
